'use client';

import React from 'react';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import {
  IDailyResortLiftTickets,
  IRelatedNews, IResortPageInfo, IResortPageSnowInfo, IResortPathInfo, ISeasonResortLiftTickets,
} from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SkipassesLiftTicketsInfo } from '@/components/region/SkipassesLiftTicketsInfo';
import {
  DailyTicketsTable, hasDailyTickets, hasSeasonPasses, SeasonPassesTable,
} from '@/components/resort/ResortLiftTicketsGrid';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { Heading } from '@/components/ui/heading';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import SpecialNote from '@/components/resort/SpecialNoteBlock';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Side } from '@/components/resort/Layout/Side';
import { Main } from '@/components/resort/Layout/Main';
import { IResortLayout } from '@/page-components/resort/_layout';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { formatDateFromStr } from '@/util/formatDate';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';

export type IResortLiftTicketsProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  seasonLiftTickets: ISeasonResortLiftTickets
  dailyLiftTickets: IDailyResortLiftTickets
  relatedNews: IRelatedNews | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-lift-tickets-page';
export const TEMPLATE_ID = '3.2.6';
export const TEMPLATE_NAME = 'Resort Lift Tickets';

export default function ResortLiftTickets({
  seasonLiftTickets,
  dailyLiftTickets,
  relatedNews,
  pathInfo,
  fullResort,
}: IResortLiftTicketsProps) {
  const { t } = useOTSTranslation('common');

  const updatedTimeDailyPasses = dailyLiftTickets.lastModified
    ? formatDateFromStr(dailyLiftTickets.lastModified)
    : '';
  const updatedTimeSeasonPasses = seasonLiftTickets.lastModified
    ? formatDateFromStr(seasonLiftTickets.lastModified)
    : '';

  const showSeasonBlock = hasSeasonPasses(seasonLiftTickets) || !!seasonLiftTickets.note || !!seasonLiftTickets.purchaseUrl;
  const showDailyBlock = hasDailyTickets(dailyLiftTickets) || !!dailyLiftTickets.note || !!dailyLiftTickets.purchaseUrl;

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={t('resort.liftTickets.seoTitle', { resort: pathInfo.title })}>
            {t('resort.liftTickets.seoText', { resort: pathInfo.title })}
          </SeoSection>
          {showSeasonBlock && (
            <section>
              <Heading
                level={2}
                className="mb-4"
              >
                {t('resort.liftTickets.seasonPasses', { resort: pathInfo.title })}
              </Heading>
              <SeasonPassesTable seasonLiftTickets={seasonLiftTickets} />
              <SpecialNote
                note={seasonLiftTickets.note}
                purchaseUrl={seasonLiftTickets.purchaseUrl}
                disclaimer={t('resort.seasonPass.disclaimer', { passlastupdated: updatedTimeSeasonPasses })}
                isPartner={fullResort.isPartner}
              />
            </section>
          )}
          {showDailyBlock && (
            <section>
              <Heading
                level={2}
                className="mb-4"
              >
                {t('resort.liftTickets.dailyTickets', { resort: pathInfo.title })}
              </Heading>
              <DailyTicketsTable dailyLiftTickets={dailyLiftTickets} />
              <SpecialNote
                note={dailyLiftTickets.note}
                purchaseUrl={dailyLiftTickets.purchaseUrl}
                disclaimer={t('resort.seasonPass.disclaimer', { passlastupdated: updatedTimeDailyPasses })}
                isPartner={fullResort.isPartner}
              />
            </section>
          )}
          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
            resortId={pathInfo.uuid}
          />
          <PlanYourTrip
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            resort={pathInfo}
            liftsUrl={fullResort.liftsUrl}
            rentalUrl={fullResort.rentalUrl}
            lessonsUrl={fullResort.lessonsUrl}
            isPartner={fullResort.isPartner}
            resortCountry={fullResort?.region?.country_code}
          />
          <SkipassesLiftTicketsInfo
            header={t('resort.liftTickets.passSeoInfoTitle', { resort: pathInfo.title })}
            seoText={t('resort.liftTickets.passSeoInfoText', {
              resort: pathInfo.title,
              opendate: formatDateFromStr(fullResort.status.openingDate),
              closedate: formatDateFromStr(fullResort.status.closingDate),
              runs: fullResort.runs?.total,
              lifts: fullResort.lifts?.total,
              phone: fullResort.phone,
            })}
          />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./lift-tickets.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
